import { memo } from 'react';
import { createPortal } from 'react-dom';
import { prefersReducedMotion } from '../utils/performance';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'gray';
  fullScreen?: boolean;
  message?: string;
}

/**
 * Reusable loading spinner component with different sizes and colors
 * Optimized for performance and accessibility
 */
export const LoadingSpinner = memo(function LoadingSpinner({
  size = 'md',
  color = 'primary',
  fullScreen = false,
  message,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  const colorClasses = {
    primary: 'border-indigo-600',
    white: 'border-white',
    gray: 'border-gray-600',
  };

  // Respect user's motion preferences
  const shouldAnimate = !prefersReducedMotion();

  const spinner = (
    <div
      className="flex flex-col items-center justify-center space-y-3"
      role="status"
      aria-live="polite"
    >
      <div
        className={`rounded-full border-b-2 ${sizeClasses[size]} ${colorClasses[color]} ${
          shouldAnimate ? 'animate-spin' : ''
        }`}
        aria-hidden="true"
      />
      {message && (
        <p className="text-sm text-gray-600" id="loading-message">
          {message}
        </p>
      )}
      <span className="sr-only">{message || 'Loading content, please wait...'}</span>
    </div>
  );

  if (fullScreen) {
    return createPortal(
      <div className="fixed inset-0 flex items-center justify-center bg-white/75 backdrop-blur-xs z-50">
        {spinner}
      </div>,
      document.body
    );
  }

  return spinner;
});

/**
 * Loading overlay for specific sections
 */
export const LoadingOverlay = memo(function LoadingOverlay({ message }: { message?: string }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="loading-message"
    >
      <LoadingSpinner size="lg" message={message} />
    </div>
  );
});

/**
 * Inline loading indicator for buttons
 */
export const ButtonSpinner = memo(function ButtonSpinner() {
  const shouldAnimate = !prefersReducedMotion();

  return (
    <svg
      className={`h-4 w-4 text-white ${shouldAnimate ? 'animate-spin' : ''}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
});

/**
 * Skeleton loader for content placeholders
 */
export const SkeletonLoader = memo(function SkeletonLoader({ lines = 3 }: { lines?: number }) {
  const shouldAnimate = !prefersReducedMotion();

  return (
    <div
      className={`space-y-3 ${shouldAnimate ? 'animate-pulse' : ''}`}
      role="status"
      aria-label="Loading content"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-4 bg-gray-200 rounded" aria-hidden="true" />
      ))}
      <span className="sr-only">Loading content, please wait...</span>
    </div>
  );
});
